import type { CSSProperties } from 'react';
import type { Edge } from '@xyflow/react';

import type { ClipCanvasNode, ClipCanvasResourceMediaType } from './types';
import { getAssetMainImageUrl, getAssetVoiceAudioUrl } from './referenceMedia';

export const REFERENCE_EDGE_STYLES: Record<ClipCanvasResourceMediaType, CSSProperties> = {
  image: {
    stroke: 'var(--clip-canvas-color-reference-image)',
    strokeWidth: 2,
    strokeDasharray: '6 5',
  },
  audio: {
    stroke: 'var(--clip-canvas-color-reference-audio)',
    strokeWidth: 2,
    strokeDasharray: '6 5',
  },
};

export function getReferenceEdgeClassName(mediaType: ClipCanvasResourceMediaType): string {
  return `clip-canvas-edge--reference-${mediaType}`;
}

export function getMediaTypeFromReferenceHandle(handleId?: string | null): ClipCanvasResourceMediaType | null {
  if (handleId?.startsWith('reference:image')) return 'image';
  if (handleId?.startsWith('reference:audio')) return 'audio';
  return null;
}

function getResourceIdFromReferenceHandle(
  handleId: string | null | undefined,
  mediaType: ClipCanvasResourceMediaType,
): string | undefined {
  const prefix = `reference:${mediaType}:`;
  if (!handleId?.startsWith(prefix)) return undefined;
  return handleId.slice(prefix.length);
}

export function canAcceptReference(
  node: ClipCanvasNode | undefined,
  mediaType: ClipCanvasResourceMediaType,
): boolean {
  if (node?.data.kind !== 'form-generator') return false;

  if (mediaType === 'image') {
    return node.data.formType === 'start-image'
      || node.data.formType === 'end-image'
      || node.data.formType === 'storyboard-image'
      || node.data.formType === 'video';
  }

  return node.data.formType === 'audio'
    || node.data.formType === 'audio-edit'
    || node.data.formType === 'video'
    || node.data.formType === 'lip-sync';
}

export function canAcceptReferenceHandle(
  targetHandle: string | null | undefined,
  mediaType: ClipCanvasResourceMediaType,
): boolean {
  if (!targetHandle) return true;
  return targetHandle === `reference:${mediaType}`;
}

export function getConnectableReference(sourceNode: ClipCanvasNode, sourceHandle?: string | null): {
  url: string;
  mediaType: ClipCanvasResourceMediaType;
} | null {
  const data = sourceNode.data;

  if (data.kind === 'applied-media' && data.mediaType === 'image' && data.url) {
    return { url: data.url, mediaType: 'image' };
  }
  if (data.kind === 'applied-media' && data.mediaType === 'audio' && data.url) {
    return { url: data.url, mediaType: 'audio' };
  }
  if (data.kind === 'candidate-resource' && data.groupType === 'image') {
    const resourceId = getResourceIdFromReferenceHandle(sourceHandle, 'image');
    const url = (resourceId
      ? data.resources.find((resource) => resource.id === resourceId)?.resultUrl
      : data.resources.find((resource) => resource.resultUrl)?.resultUrl);
    return url ? { url, mediaType: 'image' } : null;
  }
  if (data.kind === 'candidate-resource' && data.groupType === 'audio') {
    const resourceId = getResourceIdFromReferenceHandle(sourceHandle, 'audio');
    const url = (resourceId
      ? data.resources.find((resource) => resource.id === resourceId)?.resultUrl
      : data.resources.find((resource) => resource.resultUrl)?.resultUrl);
    return url ? { url, mediaType: 'audio' } : null;
  }
  if (data.kind === 'media-resource') {
    return { url: data.url, mediaType: data.mediaType };
  }
  if (data.kind === 'setting-asset') {
    const imageUrl = getAssetMainImageUrl(data.asset, data.imageUrl);
    const audioUrl = getAssetVoiceAudioUrl(data.asset, data.audioUrl);
    if (sourceHandle === 'reference:audio') {
      return audioUrl ? { url: audioUrl, mediaType: 'audio' } : null;
    }
    if (sourceHandle === 'reference:image') {
      return imageUrl ? { url: imageUrl, mediaType: 'image' } : null;
    }
    if (imageUrl && !audioUrl) return { url: imageUrl, mediaType: 'image' };
    if (audioUrl && !imageUrl) return { url: audioUrl, mediaType: 'audio' };
  }

  return null;
}

export function getEdgeReference(
  edge: Edge,
  nodes: ClipCanvasNode[],
): { url: string; mediaType: ClipCanvasResourceMediaType } | null {
  const sourceNode = nodes.find((node) => node.id === edge.source);
  if (!sourceNode) return null;
  return getConnectableReference(sourceNode, edge.sourceHandle);
}
